import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../components/AppShell";
import { api, fmtUSD } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Scale, Download, Search, ClipboardList, HelpCircle, X } from "lucide-react";
import StudyAnalysis from "../components/StudyAnalysis";

export default function Benchmark() {
  const [mode, setMode] = useState("study"); // "study" | "visit"
  const [form, setForm] = useState({ therapeutic_area: "", sponsor: "", phase: "" });
  const [result, setResult] = useState(null);
  const [ctas, setCtas] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/ctas").then((r) => setCtas(r.data));
  }, []);

  const areas = [...new Set(ctas.map((c) => c.therapeutic_area).filter(Boolean))].sort();
  const sponsors = [...new Set(ctas.map((c) => c.sponsor).filter(Boolean))].sort();
  const phases = [...new Set(ctas.map((c) => c.phase).filter(Boolean))].sort();

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      if (mode === "visit") {
        if (!form.therapeutic_area) {
          setLoading(false);
          return;
        }
        const { data } = await api.post("/ctas/benchmark", form);
        setResult({ kind: "visit", ...data });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppShell
      title="Benchmark Comparison"
      subtitle="Walk into every negotiation with study-level percentile breakdowns from your Rate Card"
    >
      {/* Mode tabs */}
      <div className="flex gap-2 mb-4" data-testid="benchmark-mode-tabs">
        <Button
          variant={mode === "study" ? "default" : "outline"}
          className={mode === "study" ? "bg-[#0A4C95] hover:bg-[#083d79]" : ""}
          onClick={() => { setMode("study"); setResult(null); }}
          data-testid="benchmark-mode-study"
        >
          <ClipboardList className="w-4 h-4 mr-1.5" /> Study Analysis (per-CTA breakdown)
        </Button>
        <Button
          variant={mode === "visit" ? "default" : "outline"}
          className={mode === "visit" ? "bg-[#0A4C95] hover:bg-[#083d79]" : ""}
          onClick={() => { setMode("visit"); setResult(null); }}
          data-testid="benchmark-mode-visit"
        >
          <Scale className="w-4 h-4 mr-1.5" /> Visit-level (Aggregate by area)
        </Button>
      </div>

      {mode === "study" && (
        <div className="space-y-5">
          <StudyAnalysisLegend />
          <StudyAnalysis ctas={ctas} />
        </div>
      )}

      {mode === "visit" && (
        <>
          <div className="bg-white border border-border rounded-md p-6 shadow-sm" data-testid="benchmark-form">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
              <div>
                <Label>Therapeutic Area *</Label>
                <Select value={form.therapeutic_area} onValueChange={(v) => setForm({ ...form, therapeutic_area: v === "__any" ? "" : v })}>
                  <SelectTrigger data-testid="benchmark-area"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {areas.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sponsor (optional)</Label>
                <Select value={form.sponsor} onValueChange={(v) => setForm({ ...form, sponsor: v === "__any" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__any">Any sponsor</SelectItem>
                    {sponsors.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Phase (optional)</Label>
                <Select value={form.phase} onValueChange={(v) => setForm({ ...form, phase: v === "__any" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__any">Any phase</SelectItem>
                    {phases.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2 flex justify-end">
                <Button onClick={run} className="bg-[#F05D23] hover:bg-[#d94e18]" data-testid="benchmark-run">
                  <Scale className="w-4 h-4 mr-1.5" /> Run Benchmark
                </Button>
              </div>
            </div>
          </div>

          {loading && <div className="mt-6 text-slate-500">Computing...</div>}
          {result?.kind === "visit" && <VisitResult result={result} />}
        </>
      )}
    </AppShell>
  );
}

function StudyAnalysisLegend() {
  const KEY = "stryde.studyAnalysisLegendDismissed";
  const [open, setOpen] = useState(() => localStorage.getItem(KEY) !== "1");

  if (!open) {
    return (
      <button
        onClick={() => { localStorage.removeItem(KEY); setOpen(true); }}
        className="text-xs text-[#0A4C95] hover:underline inline-flex items-center gap-1"
        data-testid="study-legend-reopen"
      >
        <HelpCircle className="w-3.5 h-3.5" /> How to read this analysis
      </button>
    );
  }

  const dismiss = () => { localStorage.setItem(KEY, "1"); setOpen(false); };

  return (
    <div
      className="bg-gradient-to-br from-[#F4F7FB] to-white border border-[#0A4C95]/15 rounded-md p-5 shadow-sm relative"
      data-testid="study-analysis-legend"
    >
      <button onClick={dismiss} className="absolute top-3 right-3 text-slate-400 hover:text-slate-700" aria-label="Dismiss">
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-center gap-2 mb-3">
        <HelpCircle className="w-4 h-4 text-[#0A4C95]" />
        <div className="text-sm font-semibold text-[#0A4C95]">How to read this analysis</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-sm">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Status badges (vs P75 / P90)</div>
          <ul className="space-y-1.5">
            <li className="flex items-start gap-2"><Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px] mt-0.5 shrink-0">High</Badge><span className="text-slate-700">At or above P90 — strong, you&apos;re at the top of the market.</span></li>
            <li className="flex items-start gap-2"><Badge variant="outline" className="bg-lime-100 text-lime-800 border-lime-300 text-[10px] mt-0.5 shrink-0">Medium-High</Badge><span className="text-slate-700">Between P75 and P90 — solid, push toward P90 if leverage allows.</span></li>
            <li className="flex items-start gap-2"><Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-[10px] mt-0.5 shrink-0">Medium</Badge><span className="text-slate-700">Roughly P50–P75 — market-acceptable; counter toward P75.</span></li>
            <li className="flex items-start gap-2"><Badge variant="outline" className="bg-red-100 text-red-800 border-red-300 text-[10px] mt-0.5 shrink-0">Low</Badge><span className="text-slate-700">Below median — outlier, counter to at least P75.</span></li>
            <li className="flex items-start gap-2"><Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-300 text-[10px] mt-0.5 shrink-0">No-Data</Badge><span className="text-slate-700">No benchmark in Rate Card — add to grow your library.</span></li>
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Summary cards</div>
          <ul className="space-y-1 text-slate-700">
            <li><strong>Procedure</strong> · clinical work per visit (PE, ECG, labs, biopsy, questionnaires)</li>
            <li><strong>Personnel</strong> · time/people (SC fee, PI fee, data entry, pharmacy dispense, travel)</li>
            <li><strong>Site</strong> · administrative one-time + recurring (startup, IRB, monitoring, archiving)</li>
            <li className="text-slate-500 text-xs pt-1">Variance vs P75 = matched-negotiated minus P75 sum. Negative = below market.</li>
          </ul>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-slate-200 text-xs text-slate-500">
        💡 <strong>Tip:</strong> Use the <strong>Rate Card</strong> page in the sidebar for single-value lookups across all 448 canonical line items.
      </div>
    </div>
  );
}

/* ============================== Visit-level View ============================== */
function VisitResult({ result }) {
  return (
    <div className="mt-6 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KCard label="Matched Studies" value={result.match_count} />
        <KCard label="Avg Total Budget" value={fmtUSD(result.avg_total_budget)} />
        <KCard label="Avg PI Split" value={`${result.avg_pi_split_percent}%`} />
        <KCard label="Avg Clinic Fee" value={`${result.avg_clinic_fee_percent}%`} />
      </div>

      <div className="bg-white border border-border rounded-md shadow-sm overflow-auto">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <h3 className="font-semibold">Average Grant by Visit Code</h3>
          <Button variant="outline" size="sm" onClick={() => window.print()}><Download className="w-3.5 h-3.5 mr-1" /> Export</Button>
        </div>
        <table className="w-full text-sm stryde-table">
          <thead>
            <tr>
              <th className="text-left px-4 py-2.5">Visit Code</th>
              <th className="text-right px-4 py-2.5">Average Grant</th>
              <th className="text-right px-4 py-2.5">Sample Size</th>
            </tr>
          </thead>
          <tbody>
            {result.visit_averages.map((v) => (
              <tr key={v.visit_code} className="border-t border-slate-100">
                <td className="px-4 py-2.5 font-semibold">{v.visit_code}</td>
                <td className="px-4 py-2.5 text-right font-mono-fin">{fmtUSD(v.avg_grant)}</td>
                <td className="px-4 py-2.5 text-right"><Badge variant="outline">{v.sample_size}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-border rounded-md shadow-sm overflow-auto">
        <div className="px-5 py-4 border-b border-border"><h3 className="font-semibold">Matched Studies</h3></div>
        <table className="w-full text-sm stryde-table">
          <thead>
            <tr><th className="text-left px-4 py-2.5">Study</th><th className="text-left px-4 py-2.5">Sponsor</th><th className="text-right px-4 py-2.5">Total Budget</th></tr>
          </thead>
          <tbody>
            {result.matched_studies.map((s) => (
              <tr key={s.id} className="border-t border-slate-100">
                <td className="px-4 py-2.5 font-medium">{s.study_name}</td>
                <td className="px-4 py-2.5">{s.sponsor}</td>
                <td className="px-4 py-2.5 text-right font-mono-fin">{fmtUSD(s.total_budget)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function KCard({ label, value }) {
  return (
    <div className="bg-white border border-border rounded-md p-5 shadow-sm">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="font-mono-fin text-2xl font-semibold mt-2">{value}</div>
    </div>
  );
}
